import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Clock, RefreshCw, Download, FileText, MessageSquarePlus, Check, X, Pencil, ChevronUp, ChevronDown, AlertTriangle } from 'lucide-react';
import { downloadIndoorXlsx, downloadIndoorPdf } from '../utils/indoorReport';
import DriverAvatar, { fetchDriverPhotos } from '../components/DriverPhoto';

// Indoor Time report — for each driver who worked a shift in the chosen range, the
// time from START of shift through to their ONBOARD image capture (the moment they
// finished accepting parcels indoors), plus parcels onboarded and parcels/hr. Two
// tiers of comment: a DAY comment (attached to everyone who worked that day) and a
// per-driver comment on top. Exports to styled PDF + Excel with header/footer.

const OB = '/.netlify/functions/onboard-sb';
const obApi = (action, payload) =>
  fetch(OB, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then(r => r.json());
const asApi = (action, payload) =>
  fetch('/.netlify/functions/appsscript', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then(r => r.json());

const up = s => (s || '').toString().trim().toUpperCase();
const isoAddDays = (iso, n) => { const [y, m, d] = iso.split('-').map(Number); const dt = new Date(y, m - 1, d + n); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
const datesInRange = (from, to) => { const out = []; let d = from, g = 0; while (d <= to && g++ < 400) { out.push(d); d = isoAddDays(d, 1); } return out; };
const fmtDay = iso => { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }); };
const fmtFull = iso => { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }); };
const clockFromIso = iso => { if (!iso) return ''; try { return new Intl.DateTimeFormat('en-GB', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso)); } catch { return ''; } };
const clockToDec = hhmm => { const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || ''); return m ? parseInt(m[1], 10) + parseInt(m[2], 10) / 60 : null; };

const COLUMNS = [
  { key: 'date', label: 'Date', num: false },
  { key: 'login', label: 'Login', num: false },
  { key: 'name', label: 'Driver', num: false },
  { key: 'area', label: 'Area', num: false },
  { key: 'startIndoor', label: 'Start (indoor)', num: false },
  { key: 'finishIndoor', label: 'Finish (onboard)', num: false },
  { key: 'indoorHrs', label: 'Indoor hrs', num: true },
  { key: 'parcels', label: 'Parcels', num: true },
  { key: 'pph', label: 'Parcels/hr', num: true },
  { key: 'comment', label: 'Comments', num: false },
];

const disp = (row, key) => {
  const v = row[key];
  if (v == null || v === '') return '';
  if (key === 'pph') return Number(v).toFixed(1);
  if (key === 'indoorHrs') return Number(v).toFixed(2);
  if (key === 'date') return fmtDay(v);
  return String(v);
};

const PRESETS = [
  { label: 'Today', days: 1 }, { label: '3 days', days: 3 }, { label: '1 week', days: 7 },
  { label: '2 weeks', days: 14 }, { label: '3 weeks', days: 21 }, { label: '4 weeks', days: 28 },
];

export default function IndoorTimePage() {
  const today = new Date().toLocaleDateString('en-CA');
  const [from, setFrom] = useState(isoAddDays(today, -6));
  const [to, setTo] = useState(today);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);                 // built spine (no comments baked in)
  const [photos, setPhotos] = useState({});             // read-only driver profile photos, by UPPERCASE login
  const [dayComments, setDayComments] = useState({});   // { date: text }
  const [drvComments, setDrvComments] = useState({});   // { 'date|LOGIN': text }
  const [filters, setFilters] = useState({});
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('asc');
  const [busy, setBusy] = useState(false);

  // Day-comment editor
  const [dayPick, setDayPick] = useState('');
  const [dayText, setDayText] = useState('');
  // Per-driver inline editor: { key, value }
  const [editing, setEditing] = useState(null);

  const rangeDates = useMemo(() => datesInRange(from, to), [from, to]);

  const load = useCallback(async () => {
    if (from > to) { setError('The "from" date is after the "to" date.'); return; }
    setLoading(true); setError('');
    try {
      const dates = datesInRange(from, to);
      // 1. Who worked each day + their shift start (batched to avoid a call storm).
      const loginByDate = {};
      const B = 6;
      for (let i = 0; i < dates.length; i += B) {
        const chunk = dates.slice(i, i + B);
        const res = await Promise.all(chunk.map(d => asApi('getLoginCheck', { date: d }).catch(() => ({ success: false }))));
        chunk.forEach((d, j) => { loginByDate[d] = (res[j] && res[j].success) ? (res[j].rows || []) : []; });
      }
      // 2. Onboard scans across the range → main (largest) capture per driver/day.
      const or = await obApi('onboardRange', { from, to });
      if (!or.success) throw new Error(or.error || 'Could not load onboard scans');
      const mainByKey = {};
      (or.scans || []).forEach(s => {
        const k = s.scanDate + '|' + up(s.driver);
        const items = s.items == null ? -1 : s.items;
        const cur = mainByKey[k];
        if (!cur || items > cur._items || (items === cur._items && s.scannedAt < cur.scannedAt)) mainByKey[k] = { ...s, _items: items };
      });
      // 3. Comments
      const cg = await obApi('indoorCommentsGet', { from, to });
      const dayC = {}, drvC = {};
      if (cg.success) (cg.comments || []).forEach(c => { if (!c.driver) dayC[c.date] = c.comment; else drvC[c.date + '|' + up(c.driver)] = c.comment; });

      // 4. Build spine — union of "worked a shift" and "has an onboard scan".
      const spine = {};
      Object.keys(loginByDate).forEach(d => {
        (loginByDate[d] || []).filter(r => r.status === 'FINISHED' || r.status === 'ON_SHIFT').forEach(r => {
          const login = up(r.login);
          spine[d + '|' + login] = { date: d, login, name: r.name || login, area: up(r.area || ''), startIndoor: r.shiftStart || '' };
        });
      });
      Object.keys(mainByKey).forEach(k => {
        if (!spine[k]) { const [d, login] = k.split('|'); spine[k] = { date: d, login, name: login, area: up(mainByKey[k].area || ''), startIndoor: '' }; }
      });

      const built = Object.keys(spine).map(k => {
        const sp = spine[k];
        const m = mainByKey[k];
        const finishIndoor = m ? clockFromIso(m.scannedAt) : '';
        const parcels = m ? (m.items == null ? null : m.items) : null;
        const sDec = clockToDec(sp.startIndoor), fDec = clockToDec(finishIndoor);
        let indoorHrs = (sDec != null && fDec != null) ? fDec - sDec : null;
        if (indoorHrs != null && indoorHrs < 0) indoorHrs = null;
        if (indoorHrs != null) indoorHrs = Math.round(indoorHrs * 100) / 100;
        const pph = (parcels != null && indoorHrs && indoorHrs > 0) ? Math.round((parcels / indoorHrs) * 10) / 10 : null;
        return { key: k, ...sp, finishIndoor, parcels, indoorHrs, pph };
      });
      setRows(built); setDayComments(dayC); setDrvComments(drvC);
    } catch (e) { setError(e.message); setRows([]); }
    setLoading(false);
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  // Read-only driver profile photos for the report rows.
  useEffect(() => {
    const logins = [...new Set((rows || []).map(x => (x.login || x.driver || x.loginName || x.driverLogin || '').toUpperCase()).filter(Boolean))];
    if (logins.length) fetchDriverPhotos(logins).then(p => setPhotos(prev => ({ ...p, ...prev })));
  }, [rows]);

  const commentFor = useCallback((r) => {
    const day = dayComments[r.date] || '';
    const drv = drvComments[r.date + '|' + r.login] || '';
    return [day, drv].filter(Boolean).join(' · ');
  }, [dayComments, drvComments]);

  // Rows with resolved comment, filtered + sorted.
  const viewRows = useMemo(() => {
    let out = rows.map(r => ({ ...r, comment: commentFor(r) }));
    Object.keys(filters).forEach(k => {
      const q = (filters[k] || '').trim().toLowerCase();
      if (!q) return;
      out = out.filter(r => disp(r, k).toLowerCase().includes(q));
    });
    const col = COLUMNS.find(c => c.key === sortKey) || COLUMNS[0];
    out.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (col.num) { av = av == null ? -Infinity : av; bv = bv == null ? -Infinity : bv; return sortDir === 'asc' ? av - bv : bv - av; }
      if (sortKey === 'date') { const c = a.date < b.date ? -1 : a.date > b.date ? 1 : (a.name < b.name ? -1 : 1); return sortDir === 'asc' ? c : -c; }
      av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase();
      const c = av < bv ? -1 : av > bv ? 1 : 0; return sortDir === 'asc' ? c : -c;
    });
    return out;
  }, [rows, filters, sortKey, sortDir, commentFor]);

  const totals = useMemo(() => {
    const withParcels = viewRows.filter(r => r.parcels != null);
    const pphVals = viewRows.filter(r => r.pph != null).map(r => r.pph);
    return {
      rows: viewRows.length,
      parcels: withParcels.reduce((a, r) => a + r.parcels, 0),
      avgPph: pphVals.length ? (pphVals.reduce((a, b) => a + b, 0) / pphVals.length) : null,
      noOnboard: viewRows.filter(r => !r.finishIndoor).length,
    };
  }, [viewRows]);

  const toggleSort = (k) => { if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(k); setSortDir('asc'); } };
  const setPreset = (days) => { setFrom(isoAddDays(today, -(days - 1))); setTo(today); };

  async function saveComment(date, driver, text) {
    setBusy(true);
    try {
      const r = await obApi('indoorCommentSet', { date, driver, comment: text });
      if (!r.success) throw new Error(r.error || 'Save failed');
      if (!driver) setDayComments(m => { const n = { ...m }; if (text.trim()) n[date] = text; else delete n[date]; return n; });
      else setDrvComments(m => { const n = { ...m }; const key = date + '|' + up(driver); if (text.trim()) n[key] = text; else delete n[key]; return n; });
    } catch (e) { window.alert(e.message); }
    setBusy(false);
  }

  const exportRows = () => viewRows.map(r => ({ ...r, comment: commentFor(r) }));
  const meta = () => ({ rangeLabel: `${fmtFull(from)} – ${fmtFull(to)}`, generatedAt: `Generated ${new Date().toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })}` });
  const doXlsx = async () => { setBusy(true); try { await downloadIndoorXlsx({ rows: exportRows(), ...meta() }); } catch (e) { window.alert('Excel export failed: ' + e.message); } setBusy(false); };
  const doPdf = async () => { setBusy(true); try { await downloadIndoorPdf({ rows: exportRows(), ...meta() }); } catch (e) { window.alert('PDF export failed: ' + e.message); } setBusy(false); };

  const card = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg, 12px)', padding: 16, marginBottom: 14 };
  const btn = (primary) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', cursor: busy ? 'wait' : 'pointer', border: primary ? 'none' : '1px solid var(--hp-border)', background: primary ? 'var(--hp-gold)' : 'var(--hp-surface)', color: primary ? '#1a1a1a' : 'var(--hp-text)' });
  const inputStyle = { padding: '7px 9px', borderRadius: 7, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.82rem', fontFamily: 'inherit' };

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, padding: '4px 0 14px' }}>
        <div>
          <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--hp-gold)', display: 'flex', alignItems: 'center', gap: 8 }}><Clock size={20} /> Indoor Time Report</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 2 }}>Start of shift → onboard image capture, with parcels/hr and comments.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btn(false)} disabled={busy || !viewRows.length} onClick={doPdf}><FileText size={15} /> PDF</button>
          <button style={btn(true)} disabled={busy || !viewRows.length} onClick={doXlsx}><Download size={15} /> Excel</button>
        </div>
      </div>

      {/* Range controls */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>From <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} style={{ ...inputStyle, marginLeft: 4 }} /></label>
          <label style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>To <input type="date" value={to} min={from} max={today} onChange={e => setTo(e.target.value)} style={{ ...inputStyle, marginLeft: 4 }} /></label>
          <button style={btn(false)} onClick={load} disabled={loading}><RefreshCw size={14} style={loading ? { animation: 'spin 0.9s linear infinite' } : undefined} /> {loading ? 'Loading…' : 'Refresh'}</button>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginLeft: 'auto' }}>
            {PRESETS.map(p => <button key={p.label} onClick={() => setPreset(p.days)} style={{ ...btn(false), padding: '6px 10px', fontSize: '0.74rem' }}>{p.label}</button>)}
          </div>
        </div>
      </div>

      {error && <div style={{ ...card, color: '#ef4444', fontSize: '0.84rem' }}>{error}</div>}

      {/* Day comment editor */}
      <div style={card}>
        <div style={{ fontWeight: 800, fontSize: '0.9rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><MessageSquarePlus size={16} color="var(--hp-gold)" /> Day comment</div>
        <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginBottom: 8 }}>Applies to every driver who worked that day (e.g. late product arrival, scheduled meeting). Add per-driver notes on the rows below.</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={dayPick} onChange={e => { setDayPick(e.target.value); setDayText(dayComments[e.target.value] || ''); }} style={inputStyle}>
            <option value="">Select a day…</option>
            {rangeDates.map(d => <option key={d} value={d}>{fmtDay(d)}{dayComments[d] ? '  ✓' : ''}</option>)}
          </select>
          <input value={dayText} onChange={e => setDayText(e.target.value)} placeholder="Comment for all drivers that day" disabled={!dayPick} style={{ ...inputStyle, flex: 1, minWidth: 220 }} />
          <button style={btn(true)} disabled={!dayPick || busy} onClick={() => saveComment(dayPick, '', dayText)}>Save</button>
        </div>
        {Object.keys(dayComments).length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {Object.keys(dayComments).sort().map(d => (
              <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.76rem' }}>
                <span style={{ fontWeight: 700, minWidth: 96 }}>{fmtDay(d)}</span>
                <span style={{ flex: 1, color: 'var(--hp-dim)' }}>{dayComments[d]}</span>
                <button onClick={() => { setDayPick(d); setDayText(dayComments[d]); }} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}><Pencil size={13} /></button>
                <button onClick={() => saveComment(d, '', '')} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}><X size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary bar */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        {[
          { label: 'Rows', value: totals.rows },
          { label: 'Parcels onboarded', value: totals.parcels.toLocaleString() },
          { label: 'Avg parcels/hr', value: totals.avgPph != null ? totals.avgPph.toFixed(1) : '—' },
          { label: 'Worked, no onboard', value: totals.noOnboard, warn: totals.noOnboard > 0 },
        ].map(s => (
          <div key={s.label} style={{ flex: 1, minWidth: 140, ...card, marginBottom: 0, padding: '10px 14px' }}>
            <div style={{ fontSize: '0.64rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: s.warn ? '#f59e0b' : 'var(--hp-gold)' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', minWidth: 900 }}>
          <thead>
            <tr>
              {COLUMNS.map(c => (
                <th key={c.key} onClick={() => toggleSort(c.key)} style={{ textAlign: c.num ? 'right' : 'left', padding: '10px 10px 4px', color: 'var(--hp-dim)', fontSize: '0.64rem', textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer', whiteSpace: 'nowrap', borderBottom: '1px solid var(--hp-border)' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, justifyContent: c.num ? 'flex-end' : 'flex-start' }}>
                    {c.label}{sortKey === c.key && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                  </span>
                </th>
              ))}
            </tr>
            <tr>
              {COLUMNS.map(c => (
                <th key={c.key} style={{ padding: '4px 8px 8px', borderBottom: '1px solid var(--hp-border)' }}>
                  <input value={filters[c.key] || ''} onChange={e => setFilters(f => ({ ...f, [c.key]: e.target.value }))} placeholder="filter"
                    style={{ width: '100%', minWidth: c.key === 'comment' ? 140 : 52, boxSizing: 'border-box', padding: '4px 6px', borderRadius: 5, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.72rem' }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {viewRows.length === 0 && <tr><td colSpan={COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: 'var(--hp-dim)' }}>{loading ? 'Loading…' : 'No rows for this range/filter.'}</td></tr>}
            {viewRows.map(r => {
              const drvKey = r.date + '|' + r.login;
              const isEditing = editing && editing.key === drvKey;
              return (
                <tr key={r.key} style={{ borderBottom: '1px solid var(--hp-border)' }}>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{fmtDay(r.date)}</td>
                  <td style={{ padding: '8px 10px', fontFamily: 'var(--font-mono, monospace)', fontWeight: 700 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <DriverAvatar login={r.login} photo={photos[(r.login || '').toUpperCase()]} size={24} />
                      <span>{r.login}</span>
                    </div>
                  </td>
                  <td style={{ padding: '8px 10px' }}>{r.name}</td>
                  <td style={{ padding: '8px 10px' }}>{r.area}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.startIndoor || '—'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap', color: r.finishIndoor ? 'var(--hp-text)' : '#f59e0b' }}>
                    {r.finishIndoor || <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><AlertTriangle size={11} /> none</span>}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{r.indoorHrs != null ? r.indoorHrs.toFixed(2) : '—'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: 'var(--hp-gold)' }}>{r.parcels != null ? r.parcels.toLocaleString() : '—'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>{r.pph != null ? r.pph.toFixed(1) : '—'}</td>
                  <td style={{ padding: '6px 10px', minWidth: 200 }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <input autoFocus value={editing.value} onChange={e => setEditing({ ...editing, value: e.target.value })}
                          onKeyDown={e => { if (e.key === 'Enter') { saveComment(r.date, r.login, editing.value); setEditing(null); } if (e.key === 'Escape') setEditing(null); }}
                          style={{ flex: 1, padding: '4px 6px', borderRadius: 5, border: '1px solid var(--hp-gold)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.76rem' }} />
                        <button onClick={() => { saveComment(r.date, r.login, editing.value); setEditing(null); }} style={{ background: 'none', border: 'none', color: '#22c55e', cursor: 'pointer' }}><Check size={15} /></button>
                        <button onClick={() => setEditing(null)} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}><X size={15} /></button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                        <span style={{ flex: 1, lineHeight: 1.4 }}>
                          {dayComments[r.date] && <span style={{ color: 'var(--hp-dim)' }}>{dayComments[r.date]}</span>}
                          {dayComments[r.date] && drvComments[drvKey] && <span style={{ color: 'var(--hp-dim)' }}> · </span>}
                          {drvComments[drvKey] && <span>{drvComments[drvKey]}</span>}
                        </span>
                        <button title="Driver note" onClick={() => setEditing({ key: drvKey, value: drvComments[drvKey] || '' })} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', flexShrink: 0 }}><Pencil size={13} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}
